import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Paper,
} from "@mui/material";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import CustomTypography from "../../components/common/form/CustomTypography";
import StoreTable from "../../components/swagstore/StoreTable";
import CustomTextField from "../../components/common/textFields/TextField";

const StoreListing: React.FC = () => {
  const { swagStoreStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialPage = searchParams.get("page_no")
    ? parseInt(searchParams.get("page_no") as string, 10)
    : 1;
  const initialLimit = searchParams.get("limit")
    ? parseInt(searchParams.get("limit") as string, 10)
    : 30;
  const initialSearchText = searchParams.get("search_text") || "";

  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearchText);
  const [previousSearchTerm, setPreviousSearchTerm] =
    useState(initialSearchText);

  useEffect(() => {
    setIsLoading(true);
    swagStoreStore.setStoreListingSearchText(initialSearchText);
    swagStoreStore
      .getAllStores(initialPage, initialLimit, initialSearchText)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateQueryParams = () => {
      const params = new URLSearchParams();
      params.set("page_no", swagStoreStore.storeListingPage.toString());
      params.set("limit", swagStoreStore.storeListingLimit.toString());
      if (swagStoreStore.storeListingSearchText) {
        params.set("search_text", swagStoreStore.storeListingSearchText);
      }
      navigate({ search: params.toString() }, { replace: true });
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    swagStoreStore.storeListingPage,
    swagStoreStore.storeListingLimit,
    swagStoreStore.storeListingSearchText,
    navigate,
  ]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setIsLoading(true);
    swagStoreStore.setStoreListingPage(newPage);
    swagStoreStore
      .getAllStores(
        newPage,
        swagStoreStore.storeListingLimit,
        swagStoreStore.storeListingSearchText,
      )
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setIsLoading(true);
    swagStoreStore.setStoreListingLimit(newLimit);
    swagStoreStore
      .getAllStores(1, newLimit, swagStoreStore.storeListingSearchText)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);

    // If search becomes empty and it previously had text, automatically call API
    const trimmedQuery = query.trim();
    const trimmedPrevious = previousSearchTerm.trim();

    if (trimmedQuery.length === 0 && trimmedPrevious.length > 0) {
      // Search was cleared - automatically fetch initial listing
      setIsLoading(true);
      setSearchTerm("");
      swagStoreStore.setStoreListingSearchText("");
      swagStoreStore.setStoreListingPage(1);
      swagStoreStore
        .getAllStores(1, swagStoreStore.storeListingLimit, "")
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }

    setPreviousSearchTerm(query);
  };

  const handleStatusChange = (storeId: number | string, isActive: boolean) => {
    swagStoreStore
      .updateStoreStatus(storeId, isActive)
      .then((res) => {
        showSuccess("Store status updated successfully");
      })
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      setIsLoading(true);
      const trimmedSearchTerm = searchTerm.trim();
      setSearchTerm(trimmedSearchTerm);
      swagStoreStore.setStoreListingSearchText(trimmedSearchTerm);
      swagStoreStore.setStoreListingPage(1);
      swagStoreStore
        .getAllStores(1, swagStoreStore.storeListingLimit, trimmedSearchTerm)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Container
      sx={{
        paddingTop: 2,
      }}
      maxWidth="xl"
    >
      {isLoading ? (
        <Container
          sx={{
            minHeight: "80vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Container>
      ) : (
        <Paper
          sx={{
            padding: 5,
            paddingBottom: 3,
            paddingTop: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <CustomTypography variant="heading3" fontWeight={700}>
              Store Listing
            </CustomTypography>
          </Box>

          <Box
            sx={{
              mt: 3,
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            <CustomTextField
              placeholder="Search by store name"
              value={searchTerm}
              onSearch={(query) => handleSearch(query)}
              onKeyDown={handleKeyDown}
              width={"100%"}
            />
            {/* <Box sx={{ position: "relative", display: "inline-block" }}>
              <AppButton
                variant="grayscale"
                label="Filter"
                startIcon={<FilterAltOutlinedIcon />}
                onClick={() => {
                }}
                customStyles={{ width: "130px" }}
              />
            </Box> */}
          </Box>

          {swagStoreStore.stores.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="10rem"
              textAlign="center"
            >
              <Typography variant="h6" fontWeight="500" color="text.secondary">
                No stores found
              </Typography>
            </Box>
          )}

          {swagStoreStore.stores.length > 0 && (
            <Box>
              <StoreTable
                maxHeight={window.innerHeight - 350}
                stores={swagStoreStore.stores}
                limit={swagStoreStore.storeListingLimit}
                page_no={swagStoreStore.storeListingPage}
                total_count={swagStoreStore.storeListingTotalCount}
                total_pages={swagStoreStore.storeListingTotalPages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onStatusChange={handleStatusChange}
                canUpdateStatus={userStore.hasPermission(
                  EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                )}
              />
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default observer(StoreListing);
